import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { planService } from "@/api/services"
import type { PlanResponseDto, PlanTarget } from "@/api/types/plan.types"
import Sidebar from "@/components/Sidebar"
import FileUpload from "@/components/FileUpload"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, Plus, X } from "lucide-react"

export default function PlansListPage() {
  const navigate = useNavigate()
  const [plans, setPlans] = useState<PlanResponseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Filter states
  const [targetFilter, setTargetFilter] = useState<PlanTarget | undefined>(undefined)
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined)

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  // Form states
  const [formData, setFormData] = useState<{
    name: string
    description: string
    target: PlanTarget
    creditAmount: string
    adQuotaMotoCount: string
    adQuotaGearCount: string
    adQuotaPeriodDays: string
    priceRsd: string
    backgroundColor: string
    iconUploadedFileId: string
    isActive: boolean
    sequence: string
  }>({
    name: "",
    description: "",
    target: "Basic",
    creditAmount: "",
    adQuotaMotoCount: "",
    adQuotaGearCount: "",
    adQuotaPeriodDays: "",
    priceRsd: "",
    backgroundColor: "#FF5733",
    iconUploadedFileId: "",
    isActive: true,
    sequence: "",
  })
  const [saving, setSaving] = useState(false)

  // Auto-apply filters when they change
  useEffect(() => {
    loadPlans()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetFilter, isActiveFilter])

  const loadPlans = async () => {
    setLoading(true)
    setError("")
    try {
      const params: { target?: PlanTarget; isActive?: boolean } = {}
      if (targetFilter) {
        params.target = targetFilter
      }
      if (isActiveFilter !== undefined) {
        params.isActive = isActiveFilter
      }

      const response = await planService.getPlansAdmin(params)
      setPlans(response)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load plans"
      setError(errorMessage)
      setPlans([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
      setFormData({
        name: "",
        description: "",
        target: "Basic",
        creditAmount: "",
        adQuotaMotoCount: "",
        adQuotaGearCount: "",
        adQuotaPeriodDays: "",
        priceRsd: "",
        backgroundColor: "#FF5733",
        iconUploadedFileId: "",
        isActive: true,
        sequence: "",
      })
      setCreateDialogOpen(true)
  }

  const handlePlanClick = (plan: PlanResponseDto) => {
    navigate(`/plans/${plan.id}`)
  }

  const handleSubmitCreate = async () => {
    if (!formData.name.trim()) {
      setError("Name is required")
      return
    }

    if (!formData.description.trim()) {
      setError("Description is required")
      return
    }

    if (
      !formData.creditAmount ||
      isNaN(Number(formData.creditAmount)) ||
      Number(formData.creditAmount) < 0
    ) {
      setError("Valid credit amount is required")
      return
    }

    if (formData.target === "Company") {
      if (
        !formData.adQuotaMotoCount ||
        isNaN(Number(formData.adQuotaMotoCount)) ||
        Number(formData.adQuotaMotoCount) < 0
      ) {
        setError("Valid ad quota moto count is required")
        return
      }

      if (
        !formData.adQuotaGearCount ||
        isNaN(Number(formData.adQuotaGearCount)) ||
        Number(formData.adQuotaGearCount) < 0
      ) {
        setError("Valid ad quota gear count is required")
        return
      }

      if (
        !formData.adQuotaPeriodDays ||
        isNaN(Number(formData.adQuotaPeriodDays)) ||
        Number(formData.adQuotaPeriodDays) < 0
      ) {
        setError("Valid ad quota period days is required")
        return
      }
    }

    if (
      !formData.priceRsd ||
      isNaN(Number(formData.priceRsd)) ||
      Number(formData.priceRsd) < 0
    ) {
      setError("Valid price (RSD) is required")
      return
    }

    if (
      !formData.sequence ||
      isNaN(Number(formData.sequence)) ||
      Number(formData.sequence) < 0
    ) {
      setError("Valid sequence is required")
      return
    }

    setSaving(true)
    setError("")
    setSuccess("")

    try {
      await planService.createPlan({
        name: formData.name.trim(),
        description: formData.description.trim(),
        target: formData.target,
        creditAmount: Number(formData.creditAmount),
        adQuotaMotoCount: formData.adQuotaMotoCount ? Number(formData.adQuotaMotoCount) : undefined,
        adQuotaGearCount: formData.adQuotaGearCount ? Number(formData.adQuotaGearCount) : undefined,
        adQuotaPeriodDays: formData.adQuotaPeriodDays ? Number(formData.adQuotaPeriodDays) : undefined,
        priceRsd: Number(formData.priceRsd),
        backgroundColor: formData.backgroundColor,
        iconUploadedFileId: formData.iconUploadedFileId.trim() || undefined,
        isActive: formData.isActive,
        sequence: Number(formData.sequence),
      })
      setSuccess("Plan created successfully")
      setCreateDialogOpen(false)
      setFormData({
        name: "",
        description: "",
        target: "Basic",
        creditAmount: "",
        adQuotaMotoCount: "",
        adQuotaGearCount: "",
        adQuotaPeriodDays: "",
        priceRsd: "",
        backgroundColor: "#FF5733",
        iconUploadedFileId: "",
        isActive: true,
        sequence: "",
      })
      loadPlans()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to create plan"
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleClearFilters = () => {
    setTargetFilter(undefined)
    setIsActiveFilter(undefined)
  }

  const hasActiveFilters = targetFilter !== undefined || isActiveFilter !== undefined

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Plans</h1>
              <p className="text-muted-foreground">Manage subscription plans in the system</p>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Plan
            </Button>
          </div>

          {/* Success message */}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Filters */}
          <Card className="p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="target">Target</Label>
                <select
                  id="target"
                  value={targetFilter || ""}
                  onChange={(e) =>
                    setTargetFilter(
                      e.target.value === "" ? undefined : (e.target.value as PlanTarget)
                    )
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">All Targets</option>
                  <option value="Basic">Basic</option>
                  <option value="Company">Company</option>
                  <option value="BasicAndCompany">Basic and Company</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="isActive">Status</Label>
                <select
                  id="isActive"
                  value={isActiveFilter === undefined ? "" : isActiveFilter.toString()}
                  onChange={(e) => {
                    const value = e.target.value
                    setIsActiveFilter(value === "" ? undefined : value === "true")
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div className="flex items-end">
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" onClick={handleClearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Plans Table */}
          <Card>
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !plans || plans.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p className="mb-2">No plans found</p>
                {hasActiveFilters && (
                  <p className="text-xs">Try clearing your filters to see all plans</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto px-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Price (RSD)</TableHead>
                      <TableHead>Credits</TableHead>
                      <TableHead>Ad Quota</TableHead>
                      <TableHead>Sequence</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {plans.map((plan) => (
                      <TableRow
                        key={plan.id}
                        className="cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => handlePlanClick(plan)}
                      >
                        <TableCell className="font-medium">{plan.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{plan.target}</Badge>
                        </TableCell>
                        <TableCell>{plan.priceRsd} RSD</TableCell>
                        <TableCell>{plan.creditAmount}</TableCell>
                        <TableCell>
                          {plan.adQuotaMotoCount} Moto / {plan.adQuotaGearCount} Gear / {plan.adQuotaPeriodDays} days
                        </TableCell>
                        <TableCell>{plan.sequence}</TableCell>
                        <TableCell>
                          <Badge variant={plan.isActive ? "default" : "secondary"}>
                            {plan.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(plan.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-muted-foreground text-sm">Click to view</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      </main>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Plan</DialogTitle>
            <DialogDescription>Add a new subscription plan to the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="create-name"
                  placeholder="e.g., Premium Package"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-target">
                  Target <span className="text-destructive">*</span>
                </Label>
                <select
                  id="create-target"
                  value={formData.target}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      target: e.target.value as PlanTarget,
                    })
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="Basic">Basic</option>
                  <option value="Company">Company</option>
                  <option value="BasicAndCompany">Basic and Company</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-description">
                Description <span className="text-destructive">*</span>
              </Label>
              <Input
                id="create-description"
                placeholder="e.g., Best value for professionals"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-priceRsd">
                  Price (RSD) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="create-priceRsd"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="2999.99"
                  value={formData.priceRsd}
                  onChange={(e) => setFormData({ ...formData, priceRsd: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-creditAmount">
                  Credit Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="create-creditAmount"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="100"
                  value={formData.creditAmount}
                  onChange={(e) => setFormData({ ...formData, creditAmount: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-adQuotaMotoCount">
                  Ad Quota Moto Count {formData.target === "Company" && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="create-adQuotaMotoCount"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="5"
                  value={formData.adQuotaMotoCount}
                  onChange={(e) =>
                    setFormData({ ...formData, adQuotaMotoCount: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-adQuotaGearCount">
                  Ad Quota Gear Count {formData.target === "Company" && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="create-adQuotaGearCount"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="5"
                  value={formData.adQuotaGearCount}
                  onChange={(e) =>
                    setFormData({ ...formData, adQuotaGearCount: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-adQuotaPeriodDays">
                  Ad Quota Period (Days) {formData.target === "Company" && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="create-adQuotaPeriodDays"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="30"
                  value={formData.adQuotaPeriodDays}
                  onChange={(e) =>
                    setFormData({ ...formData, adQuotaPeriodDays: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-sequence">
                  Sequence <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="create-sequence"
                  type="number"
                  step="1"
                  min="0"
                  placeholder="1"
                  value={formData.sequence}
                  onChange={(e) => setFormData({ ...formData, sequence: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-backgroundColor">
                  Background Color <span className="text-destructive">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="create-backgroundColor"
                    type="color"
                    value={formData.backgroundColor}
                    onChange={(e) =>
                      setFormData({ ...formData, backgroundColor: e.target.value })
                    }
                    className="h-10 w-20"
                  />
                  <Input
                    type="text"
                    value={formData.backgroundColor}
                    onChange={(e) =>
                      setFormData({ ...formData, backgroundColor: e.target.value })
                    }
                    placeholder="#FF5733"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <FileUpload
                label="Icon (Optional)"
                description="Upload an icon for this plan"
                value={formData.iconUploadedFileId || undefined}
                onChange={(fileId) =>
                  setFormData({ ...formData, iconUploadedFileId: fileId || "" })
                }
                previewSize="sm"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="create-isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="create-isActive" className="cursor-pointer">
                  Active
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSubmitCreate} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

